<template>
  <div>
    <div
      v-if="condensed"
      class="margin-bottom-m large-margin-bottom-z"
    >
      <button @click="handleStoreDirectorySlideout">
        <span class="link-body">See it In Store</span>
      </button>
    </div>

    <Skeleton
      v-else-if="isSkeletonVisible"
      height="40px"
    />

    <div
      v-else
      class="grid-x margin-bottom-s"
    >
      <span class="margin-right-xxs store-directory" />
      <div>
        <p class="medium">
          See it in person
        </p>

        <p v-if="zipCode && storeNameToShow">
          This item is on display at
          <button @click="handleStoreDirectorySlideout">
            <span class="link-body">{{ storeNameToShow }}</span>
          </button>
        </p>

        <p v-else-if="zipCode && !storeNameToShow">
          This item isn't on display within 100 miles of
          <button @click="handleStoreDirectorySlideout">
            <span class="link-body">{{ zipCode }}</span>
          </button>
        </p>

        <p v-else>
          Enter your
          <button @click="handleStoreDirectorySlideout">
            <span class="link-body">zip code</span>
          </button>
          to see in-store display availability.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  onMounted, computed, watch, markRaw, h, ref, defineAsyncComponent,
} from 'vue';
import { useStore } from 'vuex';
import { useMounted } from '@vueuse/core';
import Skeleton from '@common-vue/atomic-ui/src/components/skeleton';
import useBopsData from '@composables/useBopsData';
import { useBrand } from '@composables/useBrand';
import { useAvailabilityBigTicket } from '@modules/availability';
import { usePageData } from '@composables/usePageData';
import { useSlideout } from '@composables/utils/useSlideout';
import { useEnvProps } from '@composables/useEnvProps';
import { useUpc } from '@modules/upc';
import { useProductFlags } from '@modules/featureControl/composables/useProductFlags';

import { storeLocatorParams } from 'config';

const Locator = defineAsyncComponent(() => import(/* webpackChunkName: 'Locator' */ '@common-vue/locator/src/components/Locator'));

const props = defineProps({
  productId: {
    type: String,
    default() {
      const { productId } = usePageData();
      return productId;
    },
  },
  condensed: {
    type: Boolean,
    default: false,
  },
});

const { fccMacysApiKey } = useEnvProps();

const { zipCode, dataLoadNeeded } = useAvailabilityBigTicket(); // ship it zipcode

const { showSlideout } = useSlideout();
const store = useStore();

/** @type {import('vue').Ref<LocatorStoreDetails | null>} */
const selectedStoreDetails = ref(null);

const { fetch, nearestStore, result } = useBopsData({ zipCode });

onMounted(fetch);
watch(zipCode, () => {
  fetch();
  selectedStoreDetails.value = null;
});
const nearestStoreName = computed(() => nearestStore.value?.details?.name);
const nearestStoreZipCode = computed(() => nearestStore.value?.address?.zipCode);
const nearestStoreId = computed(() => nearestStore.value?.identifier?.storeLocation);

const { isMcom } = useBrand();
const { isBigTicketItemFlag } = useProductFlags();
const { selectedUpcNumber } = useUpc(props.productId);

const selectedStoreName = computed(() => selectedStoreDetails.value?.name);
const selectedStoreId = computed(() => selectedStoreDetails.value?.locationNumber || nearestStoreId.value);
const selectedStoreAddress = computed(() => selectedStoreDetails.value?.address);

const storeNameToShow = computed(() => selectedStoreName?.value
         || (nearestStoreName?.value ? `${isMcom ? "Macy's" : "Bloomingdale's"} ${nearestStoreName?.value}` : ''));

const getLocatorProps = () => ({
  address: selectedStoreAddress?.value || zipCode?.value || nearestStoreZipCode.value,
  upcNumber: selectedUpcNumber.value,
  useFccApi: true,
  fccMacysApiKey,
  divisionNumber: storeLocatorParams.divisionNumber,
  productId: props.productId,
  btItems: isBigTicketItemFlag,
  page: storeLocatorParams.page,
  typeOf: storeLocatorParams.storeLocationType,
  myStoreId: selectedStoreId.value,
});

const handleStoreSelected = async (/** @type {LocatorStoreDetails} */ storeData) => {
  selectedStoreDetails.value = storeData;
  store.commit('slideoutStore/TOGGLE', `store-directory-slideout-${props.productId}`); // Close slideout
};

const handleStoreDirectorySlideout = () => {
  showSlideout({
    id: `store-directory-slideout-${props.productId}`,
    title: 'In-store display',
    component: markRaw(() => h(Locator, {
      ...getLocatorProps(),
      onStoreSelected: handleStoreSelected,
    })),
  });
};

const isMounted = useMounted();
const isSkeletonVisible = computed(() => !isMounted.value || (dataLoadNeeded.value && !result.value));
</script>

<style lang="scss" brand="mcom" scoped>
@import '~settingSCSS';
@include title;
@import '~@core/csg/src/scss/encodedIcons/mcom/_store-directory.scss';
</style>

<style lang="scss" brand="bcom" scoped>
@import '~settingSCSS';
@include title;
</style>
